import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Radio, Plus, Users, X, ShieldAlert, ArrowRight, UserPlus, Search, Send, CalendarClock, KeyRound, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

const AnonModal = ({ onAccept }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-nightsea/80 backdrop-blur-sm" />
    <div className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
      <div className="w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-4"><ShieldAlert className="w-6 h-6" /></div>
      <h3 className="font-heading text-xl font-semibold mb-2">A safe, anonymous space</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">
        Meeting rooms are anonymous. Please keep what's shared here confidential, be respectful,
        and protect each other's privacy. By continuing you agree to keep this space safe.
      </p>
      <button data-testid="accept-anonymity" onClick={onAccept}
        className="mt-6 w-full bg-primary text-primary-foreground font-semibold py-3 rounded-full hover:bg-atlantic active:scale-95 transition-all">
        I understand — continue
      </button>
    </div>
  </div>
);

// Invite members by sending a one-tap join link via DM — no code needed.
const InviteDialog = ({ meetingId, meetingTitle, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [invited, setInvited] = useState([]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/dm/users/search?q=${encodeURIComponent(query.trim())}`); setResults(data.users || []); }
      catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const link = `${window.location.origin}/meetings/room/${meetingId}`;

  const invite = async (member) => {
    try {
      const conv = await api.post("/dm/start", { target_user_id: member.id });
      const cid = conv.data.conversation?.id || conv.data.id;
      await api.post(`/dm/conversations/${cid}/messages`, {
        message: `You're invited to join "${meetingTitle}" on The Daily Compass 🧭\nTap to join: ${link}`,
      });
      setInvited((v) => [...v, member.id]);
      toast.success(`Invite sent to ${member.display_name}`);
    } catch (e) { toast.error(errMsg(e, "Could not send invite")); }
  };

  const importContacts = async () => {
    const supported = typeof navigator !== "undefined" && "contacts" in navigator && navigator.contacts && "select" in navigator.contacts;
    if (!supported) {
      toast.info("Contact import is available on supported mobile browsers. Search members below to invite them.");
      return;
    }
    try {
      const contacts = await navigator.contacts.select(["name"], { multiple: false });
      if (contacts?.length) { setQuery(contacts[0].name?.[0] || ""); toast.info("Pick the matching member to send an invite."); }
    } catch (e) { /* user cancelled */ }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    catch (e) { toast.error("Could not copy link"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-xl font-semibold mb-1 flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> Invite people</h3>
        <p className="text-sm text-muted-foreground mb-4">They'll get a one-tap join link — no code required.</p>

        <div className="flex gap-2 mb-3">
          <button data-testid="import-contacts" onClick={importContacts} className="flex-1 flex items-center justify-center gap-2 text-sm border border-border rounded-full py-2 hover:bg-secondary transition-colors">
            <Users className="w-4 h-4" /> Import contacts
          </button>
          <button data-testid="copy-invite-link" onClick={copyLink} className="flex-1 flex items-center justify-center gap-2 text-sm border border-border rounded-full py-2 hover:bg-secondary transition-colors">
            <Send className="w-4 h-4" /> Copy link
          </button>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="invite-search-input" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search members by name…"
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
        </div>

        <div className="mt-3 max-h-64 overflow-y-auto">
          {searching ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : results.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{query ? "No members found" : "Type a name to find members"}</p>
          ) : results.map((u) => (
            <div key={u.id} data-testid="invite-result" className="flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-secondary transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={u.display_name} src={u.avatar_base64} size={38} />
                <span className="font-medium truncate">{u.display_name}</span>
              </div>
              <button data-testid={`invite-${u.id}`} onClick={() => invite(u)} disabled={invited.includes(u.id)}
                className="text-sm font-medium px-4 py-1.5 rounded-full bg-primary text-primary-foreground disabled:opacity-50 shrink-0">
                {invited.includes(u.id) ? "Invited" : "Invite"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Simplified creation — just a title, with an optional "schedule for later".
const CreateDialog = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [later, setLater] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/meetings", {
        title: title.trim(),
        scheduled_date: later ? date || null : null,
        scheduled_time: later ? time || null : null,
        meeting_type: later ? "scheduled" : "dropin",
      });
      const id = data.meeting_id || data.meeting?.meeting_id;
      toast.success("Meeting created");
      onCreated(id, title.trim());
    } catch (err) { toast.error(errMsg(err, "Could not create meeting")); }
    finally { setBusy(false); }
  };

  const inp = "w-full px-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-nightsea/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-muted-foreground"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-xl font-semibold mb-1">Start a meeting</h3>
        <p className="text-sm text-muted-foreground mb-4">Give it a name — you can invite people right after.</p>
        <input data-testid="meeting-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Evening Serenity Circle" className={inp} />

        <button type="button" onClick={() => setLater((v) => !v)} className="mt-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <CalendarClock className="w-4 h-4" /> Schedule for later <ChevronDown className={`w-4 h-4 transition-transform ${later ? "rotate-180" : ""}`} />
        </button>
        {later && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <input data-testid="meeting-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} />
            <input data-testid="meeting-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inp} />
          </div>
        )}

        <button data-testid="meeting-create-submit" type="submit" disabled={busy || !title.trim()}
          className="mt-5 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-3 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create meeting
        </button>
      </form>
    </div>
  );
};

const Meetings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [dropin, setDropin] = useState(null);
  const [needAnon, setNeedAnon] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [invite, setInvite] = useState(null); // { id, title }
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");

  const load = async () => {
    try {
      const [anon, list, drop] = await Promise.allSettled([
        api.get("/meetings/anonymity-status"), api.get("/meetings"), api.get("/meetings/dropin"),
      ]);
      if (anon.status === "fulfilled" && !anon.value.data.accepted) setNeedAnon(true);
      if (list.status === "fulfilled") setMeetings(Array.isArray(list.value.data) ? list.value.data : []);
      if (drop.status === "fulfilled") setDropin(drop.value.data);
    } catch (e) { toast.error("Could not load meetings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const accept = async () => { try { await api.post("/meetings/accept-anonymity"); setNeedAnon(false); } catch (e) { toast.error("Could not continue"); } };

  const enterRoom = async (id) => { try { await api.post(`/meetings/${id}/join`); } catch (e) {} navigate(`/meetings/room/${id}`); };

  const onCreated = (id, title) => { setShowCreate(false); load(); if (id) setInvite({ id, title }); };

  const joinByCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    try { const { data } = await api.post("/meetings/join-by-code", { code: code.trim() }); const id = data.meeting_id || data.meeting?.meeting_id; if (id) enterRoom(id); }
    catch (err) { toast.error("Invalid code"); }
  };

  const rooms = meetings.filter((m) => m.meeting_id !== "dropin-room");

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2"><Radio className="w-7 h-7 text-primary" /> Meetings</h1>
          <p className="text-muted-foreground mt-2">Join a room in one tap, or start your own and invite people.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* Primary: drop-in */}
            <button data-testid="enter-dropin" onClick={() => enterRoom(dropin?.meeting_id || "dropin-room")}
              className="w-full text-left bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/40 rounded-2xl p-6 hover:from-primary/30 transition-colors group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Always open · one tap</p>
                  <p className="font-heading text-xl font-semibold">{dropin?.title || "Open Recovery Room"}</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">{dropin?.description || "A safe, anonymous space for connection, anytime."}</p>
                </div>
                <span className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center group-hover:translate-x-0.5 transition-transform"><ArrowRight className="w-5 h-5" /></span>
              </div>
            </button>

            {/* Start a meeting */}
            <button data-testid="create-meeting-button" onClick={() => setShowCreate(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 border border-dashed border-border rounded-2xl py-4 text-foreground hover:bg-secondary/50 transition-colors font-medium">
              <Plus className="w-5 h-5 text-primary" /> Start a new meeting & invite people
            </button>

            {/* Rooms list */}
            <div className="mt-8">
              <h2 className="font-heading text-xl font-semibold mb-3">Your rooms & scheduled</h2>
              {rooms.length === 0 ? (
                <p className="text-muted-foreground py-3 text-sm">No other rooms yet. Start one above to gather your crew.</p>
              ) : (
                <div className="space-y-3" data-testid="meetings-list">
                  {rooms.map((m) => (
                    <div key={m.meeting_id} data-testid="meeting-card" className="bg-card border border-border rounded-2xl p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{m.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                            {m.scheduled_date && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {m.scheduled_date} {m.scheduled_time || ""}</span>}
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {(m.participants || []).length}/{m.max_participants || 20}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <button data-testid={`enter-${m.meeting_id}`} onClick={() => enterRoom(m.meeting_id)}
                          className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full hover:bg-atlantic transition-colors">
                          Enter <ArrowRight className="w-4 h-4" />
                        </button>
                        <button data-testid={`invite-${m.meeting_id}`} onClick={() => setInvite({ id: m.meeting_id, title: m.title })}
                          className="flex items-center gap-2 border border-border text-sm font-medium px-4 py-2 rounded-full hover:bg-secondary transition-colors">
                          <UserPlus className="w-4 h-4" /> Invite
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Secondary: join by code (collapsed) */}
            <div className="mt-8 border-t border-border pt-4">
              <button data-testid="toggle-code" onClick={() => setShowCode((v) => !v)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <KeyRound className="w-4 h-4" /> Have a private room code? <ChevronDown className={`w-4 h-4 transition-transform ${showCode ? "rotate-180" : ""}`} />
              </button>
              {showCode && (
                <form onSubmit={joinByCode} className="flex gap-2 mt-3">
                  <input data-testid="join-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code"
                    className="flex-1 px-4 py-2.5 rounded-full border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                  <button data-testid="join-code-submit" type="submit" className="px-5 rounded-full border border-border font-medium hover:bg-secondary transition-colors">Join</button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
      {needAnon && <AnonModal onAccept={accept} />}
      {showCreate && <CreateDialog onClose={() => setShowCreate(false)} onCreated={onCreated} />}
      {invite && <InviteDialog meetingId={invite.id} meetingTitle={invite.title} onClose={() => setInvite(null)} />}
    </div>
  );
};

export default Meetings;
